<template>
    <div v-if="isCompetence" class="competence-day">
        {{ $t('common.competence_day') }}: {{ routine?.versus }}
    </div>
    <div v-else ref="body" class="day-view-body" :style="bodyHeight ? { height: bodyHeight + 'px' } : null">
        <!-- LEFT: BLOCKS -->
        <div class="day-left">
            <div class="day-left-scroll">
                <routine-blocks forceCols12 :default-blocks="blocks" @change="blocks = $event" calendar-mode
                    :current-selection="currentSelection" :current-routines="currentRoutines" :collection="collection"
                    :coach-id="coachId" :routine-number="routineNumber" :date="date"
                    :assigned-routine-id="assignedRoutineId" :student-changes="studentChanges" ref="routineBlocks" />
            </div>
            <div class="day-left-footer">
                <v-btn class="save-btn black--text" color="primary" depressed block :loading="saving"
                    @click="saveRoutine">
                    {{ $t('common.save') }}
                </v-btn>
            </div>
        </div>

        <!-- MIDDLE: DAY HEADER + HISTORY + MOST USED -->
        <div class="day-mid">
            <div class="day-mid-header">
                <day-label-calendar :day="dayNumber" :day-label="dayName" is-current-month :has-events-today="!!routine" :note="note"
                    :routine="routine" :is-competence-day="isCompetence" :can-lock-day="canLockDay" is-day-view
                    :date="date" :is-student-view="isStudentView"
                    @click:note="$emit('click:note')" @click:copy="$emit('click:copy')"
                    @click:copy:week="$emit('click:copy:week')" @click:save:routine="$emit('click:save:routine')"
                    @click:save:program="$emit('click:save:program')" @click:save:block="$emit('click:save:block')"
                    @click:save:name="$emit('click:save:name')" @click:delete="$emit('click:delete')"
                    @click:lock="$emit('click:lock')" @click:add:competence="$emit('click:add:competence')"
                    @click:assign:questionnaire="$emit('click:assign:questionnaire')" />
            </div>
            <div class="day-mid-scroll">
                <div class="day-mid-history">
                    <exercises-carousel-history :routine="routine" :collection="collection" custom-title-style />
                </div>
                <div class="mid-tab-bar">
                    <button v-for="tab in midTabs" :key="tab.value" type="button" class="mid-tab"
                        :class="{ active: activeTab === tab.value }" @click="activeTab = tab.value">
                        {{ tab.label }}
                    </button>
                </div>
                <most-used-carousel v-show="activeTab === 'prescriptions'" type="prescriptions" :routine="editorRoutine"
                    @used="onUsedPrescription" list custom-title-style :coachId="coachId" />
                <most-used-carousel v-show="activeTab === 'blocks'" type="blocks" :routine="editorRoutine"
                    @used="onUsedBlock" show-block-hover list custom-title-style :coachId="coachId" />
                <most-used-carousel v-show="activeTab === 'routines'" type="routines" list custom-title-style
                    :coachId="coachId" @use:routine="onUseRoutine" />
                <most-used-carousel v-show="activeTab === 'questionnaires'" type="questionnaires" list :routine="routine"
                    custom-title-style :coachId="coachId" @use:questionnaire="onUseQuestionnaire" />
            </div>
        </div>

        <!-- RIGHT: COUNTERS -->
        <div class="day-right">
            <div class="day-right-scroll">
                <assigned-routine-counters :routine="liveRoutine" :collection="collection" :default-date="date"
                    :currentSelection="currentSelection" :coachId="coachId" :routineNumber="routineNumber" />
            </div>
        </div>

        <!-- USE ROUTINE CONFIRM -->
        <base-dialog :value="routineUseDialog" :title="$t('routines.use_routine')" :width="420"
            @input="routineUseDialog = $event" @close="routineUseDialog = false">
            <div class="routine-use-name">{{ pendingRoutine ? pendingRoutine.name : '' }}</div>
            <template v-slot:footer>
                <div class="routine-use-actions">
                    <v-btn text :disabled="applyingRoutine" @click="applyRoutine('add')">{{ $t('common.add') }}</v-btn>
                    <v-btn depressed color="primary" class="black--text" :loading="applyingRoutine"
                        @click="applyRoutine('replace')">{{ $t('routines.replace') }}</v-btn>
                </div>
            </template>
        </base-dialog>
    </div>
</template>

<script>
import { addAssignedRoutinesGroup, blockUniqueTime, clearBlocksAssignedRoutine, deleteAssignedRoutinesGroup, getPayloadBaseAssignedRoutine, updateAssignedRoutinesGroup } from '../../../utils/Exercises';
import RoutineBlocks from './RoutineBlocks.vue'
import { db } from '../../../utils/Firebase';
import AssignedRoutineCounters from './AssignedRoutineCounters.vue';
import MostUsedCarousel from '../MostUsedCarousel.vue';
import ExercisesCarouselHistory from './ExercisesCarouselHistory.vue';
import DayLabelCalendar from '../../calendar/DayLabelCalendar.vue';
import BaseDialog from '../../common/BaseDialog.vue';
import { getStudentChanges, pruneRoutineChanges } from '@/utils/Routine';
import { createSnapshotTracker, describeBlocksChange, dropHistoryEntries } from '@/utils/History';
import { CALENDAR_HISTORY } from '@/utils/CalendarHistory';
export default {
    components: { RoutineBlocks, AssignedRoutineCounters, MostUsedCarousel, ExercisesCarouselHistory, DayLabelCalendar, BaseDialog },
    name: 'DayCalendarContent',
    props: {
        date: {
            type: String,
            default: null,
        },
        currentSelection: {
            type: Object,
            default: null,
        },
        currentRoutines: {
            type: Array,
            default: null,
        },
        collection: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
        routineNumber: {
            type: Number,
            default: 1,
        },
        assignedRoutineToReplace: {
            type: Array,
            default: () => [],
        },
        note: {
            type: Object,
            default: null,
        },
        canLockDay: {
            type: Boolean,
            default: false,
        },
        isStudentView: {
            type: Boolean,
            default: false,
        },
        trackChange: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            blocks: [],
            saving: false,
            bodyHeight: null,
            activeTab: 'prescriptions',
            pendingRoutine: null,
            routineUseDialog: false,
            applyingRoutine: false,
        }
    },
    computed: {
        midTabs(vm = this) {
            return [
                { label: vm.$t('common.prescriptions'), value: 'prescriptions' },
                { label: vm.$tc('common.blocks', 2), value: 'blocks' },
                { label: vm.$t('common.routines'), value: 'routines' },
                { label: vm.$t('common.questionnaires'), value: 'questionnaires' },
            ]
        },
        routine(vm = this) {
            return vm.currentRoutines.find(routine => routine.rawDate.full === vm.date && routine.routineNumber === vm.routineNumber) || null
        },
        editorRoutine(vm = this) {
            return { blocks: vm.blocks }
        },
        studentChanges(vm = this) {
            if (vm.collection !== 'assignedRoutines') return null
            return getStudentChanges(vm.routine)
        },
        isEmptyEditor(vm = this) {
            return vm.blocks.every(block => !(block?.exercises || []).length)
        },
        liveRoutine(vm = this) {
            return vm.routine ? { ...vm.routine, blocks: vm.blocks } : vm.editorRoutine
        },
        assignedRoutineId(vm = this) {
            return vm.routine?.id || null
        },
        isCompetence(vm = this) {
            return vm.routine?.type === 'competence'
        },
        dayNumber(vm = this) {
            return vm.date ? vm.$moment(vm.date).date() : ''
        },
        dayName(vm = this) {
            return vm.date ? vm.$moment(vm.date).format('dddd') : ''
        },
    },
    created() {
        const vm = this
        vm._historyOwner = `day-${vm._uid}`
        vm._history = createSnapshotTracker({
            context: CALENDAR_HISTORY,
            owner: vm._historyOwner,
            read: () => vm.blocks,
            write: value => {
                vm.blocks = value
                vm.$nextTick(() => {
                    if (vm.$refs.routineBlocks) vm.$refs.routineBlocks.setDefaults()
                })
            },
            describe: describeBlocksChange,
        })
    },
    mounted() {
        this.updateBodyHeight()
        window.addEventListener('resize', this.updateBodyHeight)
        if (window.ResizeObserver) {
            this.bodyResizeObserver = new ResizeObserver(() => this.updateBodyHeight())
            this.bodyResizeObserver.observe(document.body)
            const header = document.querySelector('.cal-header')
            if (header) this.bodyResizeObserver.observe(header)
        }
        this._heightTimers = [150, 400, 800].map(ms => setTimeout(this.updateBodyHeight, ms))
    },
    beforeDestroy() {
        this._history.destroy()
        dropHistoryEntries(CALENDAR_HISTORY, this._historyOwner)
        window.removeEventListener('resize', this.updateBodyHeight)
        if (this.bodyResizeObserver) this.bodyResizeObserver.disconnect()
        if (this._heightTimers) this._heightTimers.forEach(clearTimeout)
    },
    methods: {
        updateBodyHeight() {
            const el = this.$refs.body
            if (!el) return
            const documentTop = el.getBoundingClientRect().top + window.scrollY
            const target = Math.max(0, Math.round(window.innerHeight - documentTop))
            if (Math.abs((this.bodyHeight || 0) - target) > 1) this.bodyHeight = target
        },
        async saveRoutine() {
            const vm = this;
            await vm.withLoading(async () => {

            let blocks = clearBlocksAssignedRoutine(vm.blocks, false)

            if (blocks.length === 0 || blocks.some(block => block.exercises.length === 0)) {
                vm.toast(vm.$t('common.no_exercises_blocks'), 'error')
                return
            }

            const persist = async () => {
                if (vm.assignedRoutineId) {
                    const assignedRoutinesIds = vm.routine?.assignedRoutinesIds || []
                    await db.collection(vm.collection).doc(vm.assignedRoutineId).update({ blocks })
                    await pruneRoutineChanges(vm.assignedRoutineId, blocks, vm.collection)
                    if (vm.collection === 'groupAssignedRoutines') await updateAssignedRoutinesGroup(assignedRoutinesIds, blocks)
                } else {
                    const name = `${vm.currentSelection.name} - ${vm.$moment(vm.date).format('DD-MM-YYYY')}`
                    let payloadAssignedRoutine = getPayloadBaseAssignedRoutine(name, vm.date, { coachId: vm.coachId }, blocks, vm.routineNumber)

                    if (vm.collection === 'assignedRoutines') {
                        payloadAssignedRoutine.studentId = vm.currentSelection.objectID
                    } else {
                        payloadAssignedRoutine.teamId = vm.currentSelection.teamId || null
                        payloadAssignedRoutine.groupId = vm.currentSelection.id
                    }

                    const arRef = await db.collection(vm.collection).add(payloadAssignedRoutine)
                    const assignedRoutineId = arRef.id

                    vm.currentRoutines.push({ id: assignedRoutineId, ...payloadAssignedRoutine })

                    if (vm.collection === 'groupAssignedRoutines') {
                        let students = [...vm.currentSelection.students]
                        if (vm.assignedRoutineToReplace.length > 0) {
                            let toDelete = vm.assignedRoutineToReplace.filter(ar => ar.replace).map(ar => {
                                return ar.assignedRoutineId
                            })
                            if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)
                            let skippedStudents = vm.assignedRoutineToReplace.filter(ar => !ar.replace).map(ar => {
                                return ar.studentId
                            })
                            if (skippedStudents.length > 0) students = students.filter(st => !skippedStudents.includes(st))
                        }
                        await addAssignedRoutinesGroup({ id: assignedRoutineId, ...payloadAssignedRoutine, date: vm.date }, students)
                    }
                }
            }

            if (vm.trackChange) await vm.trackChange(vm.$t('history.save_routine'), [vm.date], persist)
            else await persist()

            vm.toast(vm.$t('routines.saved'))
            }, 'saving')
        },
        onUsedBlock({ block, item }) {
            const vm = this;
            const index = (block || 1) - 1
            const target = vm.blocks[index]
            const isEmptyTarget = target && !(target.exercises || []).length
            vm.blocks.splice(index, isEmptyTarget ? 1 : 0, ...blockUniqueTime([item.block]))
            if (this.$refs.routineBlocks) this.$refs.routineBlocks.setDefaults()
        },
        onUsedPrescription({ block, item, exercise }) {
            const vm = this;
            let exerciseIndex = vm.blocks[block - 1].exercises.findIndex(ex => ex.uid === exercise.uid)
            vm.blocks[block - 1].exercises[exerciseIndex].config = item.config
            if (this.$refs.routineBlocks) this.$refs.routineBlocks.setDefaults(true)
        },
        onUseRoutine(item) {
            const vm = this;
            vm.pendingRoutine = item
            if (vm.isEmptyEditor) return vm.applyRoutine('replace')
            vm.routineUseDialog = true
        },
        async applyRoutine(mode) {
            const vm = this;
            vm.applyingRoutine = true
            let routineBlocks = vm.pendingRoutine?.blocks
            if (!routineBlocks && vm.pendingRoutine?.objectID) {
                const doc = await db.collection('routines').doc(vm.pendingRoutine.objectID).get()
                routineBlocks = doc.exists ? (doc.data().blocks || []) : []
            }
            const cleaned = blockUniqueTime(routineBlocks || [])
            vm.blocks = mode === 'replace' ? cleaned : [...vm.blocks, ...cleaned]
            vm.$nextTick(() => {
                if (vm.$refs.routineBlocks) vm.$refs.routineBlocks.setDefaults()
            })
            vm.applyingRoutine = false
            vm.routineUseDialog = false
        },
        onUseQuestionnaire(item) {
            this.$emit('use:questionnaire', item)
        },
    },
    watch: {
        date() {
            dropHistoryEntries(CALENDAR_HISTORY, this._historyOwner)
            this.updateBodyHeight()
        },
        routine: {
            handler(val) {
                this.blocks = blockUniqueTime(val?.blocks || [{ exercises: [] }])
                this.$nextTick(() => {
                    if (this.$refs.routineBlocks) this.$refs.routineBlocks.setDefaults()
                    this.$nextTick(() => this._history.sync())
                })
                this.updateBodyHeight()
            },
            immediate: true,
        },
        blocks: {
            handler(val) {
                if (this.routine) {
                    this.routine.blocks = val
                }
                this._history.schedule()
            },
            deep: true,
        },
        saving(val) {
            this.$emit('saving', val)
        }
    },
}
</script>

<style scoped>
.competence-day {
    text-align: center;
    padding-bottom: 12px;
}

.day-view-body {
    display: flex;
    height: calc(100vh - 260px);
    min-height: 0;
    overflow: hidden;
    border-top: 1px solid var(--v-outline-base);
}

.day-left {
    width: clamp(220px, 30%, 420px);
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--v-surface-base);
    border-right: 1px solid var(--v-outline-base);
}

.day-left-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 12px 10px;
}

.day-left-footer {
    flex-shrink: 0;
    padding: 10px;
    border-top: 1px solid var(--v-outline-base);
}

.save-btn {
    height: 38px !important;
    border-radius: 9px;
    font-weight: 700;
    letter-spacing: normal;
}

.day-mid {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--v-bg-base);
    border-right: 1px solid var(--v-outline-base);
}

.day-mid-header {
    flex-shrink: 0;
    padding: 4px 8px;
    background: var(--v-surface-base);
    --day-status-ring: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.routine-use-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    text-align: center;
}

.routine-use-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
}

.day-mid-history {
    padding: 0;
}

.mid-tab-bar {
    display: flex;
    gap: 2px;
    margin: 10px 0 8px;
    padding: 3px;
    background: var(--v-surface2-base);
    border-radius: 9px;
}

.mid-tab {
    flex: 1;
    padding: 6px 4px;
    border-radius: 7px;
    background: transparent;
    color: var(--v-txtMuted-base);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .01em;
    white-space: nowrap;
    cursor: pointer;
    transition: color .15s, background .15s;
}

.mid-tab:hover {
    color: var(--v-txtSecondary-base);
}

.mid-tab.active {
    background: var(--v-surface-base);
    color: var(--v-txtPrimary-base);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
}

.day-mid-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 12px 12px 24px;
}

.day-right {
    width: clamp(240px, 25%, 320px);
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--v-surface-base);
}

.day-right-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 0 0 76px;
}

.day-left-scroll::-webkit-scrollbar,
.day-mid-scroll::-webkit-scrollbar,
.day-right-scroll::-webkit-scrollbar {
    width: 3px;
}

.day-left-scroll::-webkit-scrollbar-thumb,
.day-mid-scroll::-webkit-scrollbar-thumb,
.day-right-scroll::-webkit-scrollbar-thumb {
    background: var(--v-outline2-base);
    border-radius: 2px;
}
</style>